import { Injectable } from '@angular/core'
import { FormArray, FormBuilder, FormGroup } from '@angular/forms'
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap'
import { every } from 'lodash'
import { filter, switchMap } from 'rxjs'
import {
    ClientUserRepresentativeDto,
    RepresentativeNotificationDto
} from '../../../../../../shared/models/setting.interfaces'
import { ConfirmModalService } from '../../../../../services/confirm-modal.service'
import { ToastService } from '../../../../../services/toast.service'
import { SettingApiService } from './setting-api.service'

@Injectable()
export class NotificationSettingsModalService {
    notificationSettingsForm!: FormGroup
    userEmail: string = ''
    userPhone: string = ''

    constructor(
        private activeModal: NgbActiveModal,
        private fb: FormBuilder,
        private toastService: ToastService,
        private confirmModalService: ConfirmModalService,
        private settingApiService: SettingApiService
    ) {}

    getDataAndInitForm(): void {
        this.settingApiService.getRepresentative().subscribe((representativeDto: ClientUserRepresentativeDto) => {
            this.userEmail = representativeDto.clientUser.email
            this.userPhone = representativeDto.clientUser.phoneNo
            this.initForm(representativeDto.representativeNotifications)
        })
    }

    saveSettings(): void {
        this.confirmModalService
            .open('setting.notifications.saveConfirm')
            .closed.pipe(
                filter((answer) => answer === true),
                switchMap(() =>
                    this.settingApiService.updateRepresentativeNotifications(this.notificationSettingsFormArray.value)
                )
            )
            .subscribe(() => {
                this.toastService.showSuccess('setting.notifications.saveSuccess')
                this.activeModal.close()
            })
    }

    close(): void {
        if (this.notificationSettingsForm.dirty) {
            this.confirmModalService
                .open('modal.messages.confirmCloseWarning')
                .closed.pipe(filter((answer) => answer === true))
                .subscribe(() => this.activeModal.close())
        } else {
            this.activeModal.close()
        }
    }

    private switchAllNotificationTo(settingFieldName: string, value: boolean): void {
        this.notificationSettingsFormArray.controls.forEach((control) => {
            control.get(settingFieldName)?.setValue(value, { emitEvent: false })
        })
    }

    private mailSettingChanged(): void {
        const allMailSettings: boolean[] = this.notificationSettingsFormArray.controls.map((e) => e.get('mail')?.value)
        this.notificationSettingsForm.get('areAllEmailNotificationsSelected')?.setValue(
            every(allMailSettings, (s) => s),
            { emitEvent: false }
        )
    }

    private smsSettingChanged(): void {
        const allSmsSettings: boolean[] = this.notificationSettingsFormArray.controls.map((e) => e.get('sms')?.value)
        this.notificationSettingsForm.get('areAllSmsNotificationsSelected')?.setValue(
            every(allSmsSettings, (s) => s),
            { emitEvent: false }
        )
    }

    private initForm(notificationSettings: RepresentativeNotificationDto[]): void {
        this.notificationSettingsForm = this.fb.group({
            areAllEmailNotificationsSelected: [every(notificationSettings, (s) => s.mail)],
            areAllSmsNotificationsSelected: [every(notificationSettings, (s) => s.sms)],
            notificationSettings: this.fb.array([])
        })
        this.notificationSettingsForm
            .get('areAllEmailNotificationsSelected')
            ?.valueChanges.subscribe((value: boolean) => this.switchAllNotificationTo('mail', value))
        this.notificationSettingsForm
            .get('areAllSmsNotificationsSelected')
            ?.valueChanges.subscribe((value: boolean) => this.switchAllNotificationTo('sms', value))

        if (notificationSettings?.length > 0) {
            const notificationSettingsFormArray: FormArray = this.notificationSettingsForm.controls[
                'notificationSettings'
            ] as FormArray
            notificationSettings.forEach((setting) => {
                const settingForm: FormGroup = this.fb.group({
                    id: [setting.id],
                    operation: [setting.operation],
                    mail: [setting.mail],
                    sms: [setting.sms]
                })
                settingForm.get('mail')?.valueChanges.subscribe(() => this.mailSettingChanged())
                settingForm.get('sms')?.valueChanges.subscribe(() => this.smsSettingChanged())
                notificationSettingsFormArray.push(settingForm)
            })
        }
    }

    get notificationSettingsFormArray(): FormArray {
        return this.notificationSettingsForm.get('notificationSettings') as FormArray
    }
}
